<script setup lang="ts">
import { computed, onMounted, type PropType } from 'vue';
import { useRouter } from 'vue-router';
import { OrganisationInvitationStatus } from '@/api';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaTabBar, { type GaiaTabBarItem } from '@/components/design/composite/GaiaTabBar.vue';
import { useOrganizationStore } from '@/stores/organizationStore';

type PeopleTab = 'members' | 'invitations' | 'roles';

const props = defineProps({
  active: {
    type: String as PropType<PeopleTab>,
    required: true,
  },
});

const router = useRouter();
const organizationStore = useOrganizationStore();

/**
 * Members, invitations and roles are one question — who is in the organisation
 * and what can they do — so they are one page. Each tab is still its own route,
 * so links, bookmarks and the back button keep working.
 */
const ROUTES: Record<PeopleTab, string> = {
  members: 'settings-users',
  invitations: 'settings-invitations',
  roles: 'settings-permissions',
};

const DESCRIPTIONS: Record<PeopleTab, string> = {
  members: 'Everyone in your organisation. Open a member to see and change their roles.',
  invitations: 'People you have invited who have not joined yet.',
  roles: 'Roles decide what members can do. Everyone has the Organisation Member role.',
};

/** Only invitations carry a count: a waiting one is the thing worth noticing. */
const pendingInvitations = computed(
  () =>
    organizationStore.invitations.filter(
      invitation => invitation.status === OrganisationInvitationStatus.PENDING
    ).length
);

const tabs = computed((): GaiaTabBarItem[] => [
  { key: 'members', label: 'Members' },
  {
    key: 'invitations',
    label: 'Invitations',
    count: pendingInvitations.value > 0 ? pendingInvitations.value : undefined,
  },
  { key: 'roles', label: 'Roles' },
]);

function select(key: string): void {
  if (key === props.active) return;
  void router.push({ name: ROUTES[key as PeopleTab] });
}

// The members tab loads invitations itself; the roles tab doesn't, but its
// Invitations count should still be right.
onMounted(() => {
  if (organizationStore.invitations.length === 0 && !organizationStore.invitationsLoading) {
    void organizationStore.fetchInvitations();
  }
});
</script>

<template>
  <n-flex vertical :size="16" :wrap="false" :style="{ marginBottom: '20px' }">
    <n-flex justify="space-between" align="flex-end" :size="16" :wrap="false">
      <n-flex vertical :size="6" :style="{ minWidth: 0, flex: '1 1 auto' }">
        <gaia-header :level="1" variant="display" size="screen" :style="{ margin: 0 }">
          People
        </gaia-header>
        <gaia-text size="body" tone="subtle">{{ DESCRIPTIONS[props.active] }}</gaia-text>
      </n-flex>
      <n-flex align="center" :size="12" :wrap="false" :style="{ flex: 'none' }">
        <slot name="actions" />
      </n-flex>
    </n-flex>
    <gaia-tab-bar :items="tabs" :active="props.active" @select="select" />
  </n-flex>
</template>
